import "./archive-drawer";
import "./archive-gallery";

function throttle(mainFunction, delay) {
  let timerFlag = null;

  return (...args) => {
    if (timerFlag === null) {
      mainFunction(...args);
      timerFlag = setTimeout(() => {
        timerFlag = null;
      }, delay);
    }
  };
}

class ArchiveSection extends HTMLElement {
  constructor() {
    super();

    this.addEventListener("click", (e) => {
      const button = e.target.closest("[data-archive-button]");
      if (button) {
        this._openGallery(button);
      }
    });

    const handleMouseMove = throttle((e) => {
      if (!this.isIntersecting) return;

      const { clientX, clientY } = e;
      const { innerHeight, innerWidth } = window;

      const xOffset = (innerWidth / 2 - clientX) / innerHeight;
      const yOffset = (innerHeight / 2 - clientY) / innerWidth;

      this.style.setProperty("--x-offset", xOffset);
      this.style.setProperty("--y-offset", yOffset);
    }, 30);

    this.addEventListener("mousemove", handleMouseMove);

    this.observer = new IntersectionObserver((entries) => {
      this.isIntersecting = entries[0].isIntersecting;
    });

    this.observer.observe(this);
  }

  async connectedCallback() {
    const collectionHandle = this.dataset.collection;
    if (!collectionHandle) return;

    const archive = this.querySelector(".archive");
    if (!archive) return;

    const allCards = [];
    const allSlides = [];

    let page = 1;
    let totalPages = 1;

    while (page <= totalPages) {
      const url = `/collections/${collectionHandle}?view=archive&page=${page}`;
      const response = await fetch(url);
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, "text/html");
      const container = doc.querySelector("[data-archive-page]");

      if (!container) break;

      if (page === 1) {
        totalPages = parseInt(container.dataset.archivePages, 10) || 1;
      }

      container.querySelectorAll("[data-archive-card]").forEach((el) => {
        const child = el.firstElementChild;
        if (child) allCards.push(child);
      });

      container.querySelectorAll("[data-archive-slide]").forEach((el) => {
        allSlides.push(el.firstElementChild?.outerHTML || "");
      });

      page++;
    }

    if (!allCards.length) return;

    // Shuffle cards and slides in sync
    for (let i = allCards.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [allCards[i], allCards[j]] = [allCards[j], allCards[i]];
      [allSlides[i], allSlides[j]] = [allSlides[j], allSlides[i]];
    }

    // Update data-index on each card to its shuffled position
    allCards.forEach((wrapper, i) => {
      const card = wrapper.querySelector("[data-product-id]");
      if (card) card.dataset.index = i;
    });

    // Store virtual slides JSON in the gallery template
    const galleryTemplate = this._galleryMarkup;
    if (galleryTemplate) {
      const slidesScript = galleryTemplate.content.querySelector(
        "[data-virtual-slides]",
      );
      if (slidesScript) slidesScript.textContent = JSON.stringify(allSlides);
    }

    // Build off-DOM then insert once to avoid per-card reflow
    const fragment = document.createDocumentFragment();
    allCards.forEach((card) => fragment.appendChild(card));

    const animateInView = document.createElement("animate-in-view");
    const configScript = document.createElement("script");
    configScript.type = "application/json";
    configScript.textContent = JSON.stringify({
      targets: [
        {
          selector: ".archive__card-wrapper",
          animation: "none",
          // stagger: 75,
          subTarget: {
            selector: ".archive-card",
            animation: "slide-up-large",
            stagger: 75,
            options: {
              delay: 75,
            },
          },
          // options: {
          //   delay: 75,
          // },
        },
      ],
    });
    animateInView.appendChild(configScript);
    fragment.appendChild(animateInView);
    archive.appendChild(fragment);
  }

  disconnectedCallback() {}

  _openGallery(trigger) {
    const gallery = this._galleryMarkup?.content.children[0];

    if (!gallery) return;

    gallery.dataset.preselectMediaIndex =
      trigger.closest("[data-index]").dataset.index;

    const html = gallery.outerHTML;
    (trigger || this).dispatchEvent(
      new CustomEvent("markup-modal:open", {
        bubbles: true,
        detail: { html, target: "archive-modal" },
      }),
    );
  }

  get _galleryMarkup() {
    return this.querySelector("[data-archive-markup]");
  }
}

if (!customElements.get("archive-section")) {
  customElements.define("archive-section", ArchiveSection);
}
